/// <reference path="../types.d.ts" />

import history from '../history';
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';

import { HttpMethods, commonEnums } from '@codametrix/ui-common';
import { api } from '../core/net';
import { showFeedback } from './feedback.actions';
import { Dictionary, DictionaryValues } from './action-types';
import { CMxComponents } from '@codametrix/ui-components/src/types';
import { USER_ACTION_METADATA } from './action-constants';
import { syncActions as dictionaryValuesSyncActions } from './dictionary-values';
import { AmplifyCore } from '@codametrix/ui-common';

const { paginationUtils } = AmplifyCore;

const { DictionaryStatus } = commonEnums;

const chooseOrg = createAction<void>(Dictionary.CHOOSE_ORG);
const chooseDictionary = createAction<number>(Dictionary.CHOOSE_DICTIONARY);
const pushPath = createAction<CMxComponents.Breadcrumb>(Dictionary.PUSH_PATH);
const saveAssociatedDictionaries = createAction<CMxAPI.Dictionary[]>(
  Dictionary.SAVE_ASSOCIATED_DICTIONARIES
);

const loadDictionaries = createAsyncThunk<any, any, { rejectValue: CMxCommonApp.SubmitError }>(
  Dictionary.TENANT_SELECTED,
  async (item, { dispatch }) => {
    const url = `/cmx/admin/orgs/${item.id}/dictionaries`;
    dispatch(chooseOrg(item));
    history.push(url);
    return item;
  }
);

const loadDictionary = createAsyncThunk<
  CMxAPI.Dictionary,
  AppProps.actionParams<CMxAPI.Dictionary>,
  { rejectValue: CMxCommonApp.SubmitError }
>(Dictionary.SELECTED, async (params, { dispatch }) => {
  const { data: dictionary, path } = params;

  const searchParms = new URLSearchParams();
  searchParms.set('tenantId', dictionary.tenantId.uuid);
  const dictionaries =
    (await api<CMxAPI.Dictionary[]>({
      endpoint: `/dictionary/${
        dictionary.name
      }/list/v2?${searchParms.toString()}`
    })) || [];

  dispatch(saveAssociatedDictionaries(dictionaries));

  const status =
    dictionary.editStatus === 'UPDATE'
      ? DictionaryStatus.DRAFT
      : dictionary.status.toLowerCase();

  dispatch(dictionaryValuesSyncActions.setStatusFilter(status));
  dispatch(chooseDictionary(dictionary.id));
  history.push(path);

  return dictionary;
});

const getDetail = createAsyncThunk<
  CMxAPI.Dictionary,
  string | number | null,
  {
    rejectValue: CMxCommonApp.SubmitError;
    fulfilledMeta: typeof USER_ACTION_METADATA;
    rejectedMeta: typeof USER_ACTION_METADATA;
  }
>(
  Dictionary.FETCH_DETAIL,
  async (id, { rejectWithValue, fulfillWithValue }) => {
    const userActionMeta = USER_ACTION_METADATA;
    if (id === undefined || id === null) {
      // TO REFACTOR - REMOVE ME.
      return fulfillWithValue({} as CMxAPI.Dictionary, userActionMeta);
    }

    try {
      const detail = await api<CMxAPI.Dictionary>({
        endpoint: `/dictionary/${id}/dictionaryvalues/v2`
      });
      return fulfillWithValue(detail, userActionMeta);
    } catch (e) {
      return rejectWithValue(e as CMxCommonApp.SubmitError, userActionMeta);
    }
  },
  {
    getPendingMeta: () => ({ ...USER_ACTION_METADATA })
  }
);

const saveItem = createAsyncThunk<
  CMxAPI.Dictionary,
  CMxAPI.Dictionary,
  { rejectValue: CMxCommonApp.SubmitError }
>(Dictionary.SAVE, async (item, { dispatch }) => {
  const isUpdate = item.id !== null;
  let method: string = isUpdate ? HttpMethods.PUT : HttpMethods.POST;

  const saved = await api<CMxAPI.Dictionary>({
    endpoint: `/dictionary/v2`,
    init: {
      method
    },
    body: item
  });

  return saved;
});

type updateMetadataParams = {
  dictionary: CMxAPI.Dictionary;
  successPath: string;
};

const updatePublishedMetadata = createAsyncThunk<
  CMxAPI.Dictionary,
  updateMetadataParams,
  { rejectValue: CMxCommonApp.SubmitError }
>(Dictionary.UPDATE_PUBLISHED_METADATA, async (params, { dispatch }) => {
  const { dictionary, successPath } = params;

  const result = await (dispatch(saveItem(dictionary)) as any).unwrap();
  // route back to dictionary value list of new draft w/ updated metadata
  dispatch(loadDictionary({ data: result, path: successPath }) as any);

  if (result) {
    dispatch(
      showFeedback({
        id: Date.now(),
        message: `Metadata updated. Publish to finalize changes.`,
        dismissable: true
      }) as any
    );
  }

  return result;
});

const listItems = createAsyncThunk<
  CMxAPI.PageableList<CMxAPI.Dictionary>,
  AppProps.listParams,
  { rejectValue: CMxCommonApp.SubmitError }
>(Dictionary.LIST, async params => {
  const searchParams = paginationUtils.searchParamsFactory(
    params.sortablePageable
  );
  const pageableList = await api<CMxAPI.PageableList<CMxAPI.Dictionary>>({
    endpoint: `/dictionary/v2?${searchParams.toString()}&tenantId=${
      params.id
    }&derivedIndicator=false&uniqueByName=true`,
    init: {
      method: HttpMethods.GET
    }
  });

  return pageableList;
});

interface tenantListItem {
  tenantName: string;
  key: string;
  id: number;
  organizationCode: string;
}
const createTenantList = (org: CMxAPI.Organization): tenantListItem[] => {
  if (org === undefined) {
    return [];
  }

  const piece: tenantListItem = {
    tenantName: org.organizationName,
    key: org.tenantId,
    id: org.id,
    organizationCode: org.organizationCode
  };

  var children: tenantListItem[] = [];
  if (org.parentLevel !== 2) {
    org.children?.forEach(child => {
      children.push(...createTenantList(child));
    });
  }

  if (children !== undefined && children.length > 0) {
    return [piece, ...children];
  }

  return [piece];
};

const getTenantList = createAsyncThunk<
  CMxCommonApp.FieldDefinition[],
  number,
  { rejectValue: CMxCommonApp.SubmitError }
>(DictionaryValues.TENANT, async (id: number) => {
  const endpoint = isNaN(id as number)
    ? `/organization/tenant/v1?tenantId=${id}`
    : `/organization/id/v1?organizationId=${id}`;
  const orgOpts = {
    endpoint: endpoint
  };
  const organization = await api<CMxAPI.Organization>(orgOpts);

  const tenantList = createTenantList(organization).sort((a, b) => {
    return a.tenantName.toUpperCase().localeCompare(b.tenantName.toUpperCase());
  });

  return tenantList;
});

export const syncActions = {
  chooseOrg,
  chooseDictionary,
  saveAssociatedDictionaries,
  pushPath
};

export {
  listItems,
  getDetail,
  saveItem,
  loadDictionary,
  getTenantList,
  loadDictionaries,
  updatePublishedMetadata
};
